<template>
  <ion-page>
    <!-- 
      <ion-header :translucent="true">
        <ion-toolbar>
          <ion-title>Login5</ion-title>
        </ion-toolbar>
      </ion-header> -->

    <ion-content
      :fullscreen="true"
      class="h-[100svh] w-screen text-center text-grey-100 font-aktiv-grotesk"
    >
      <p
        v-if="!error"
        class="w-full h-full px-[24px] flex justify-center items-center m-auto text-[32px] leading-[40px] font-aktiv-grotesk-medium"
      >
        🔒 Logging you in...
      </p>
      <div
        v-if="error"
        class="w-full h-full px-[24px] flex flex-col justify-center items-center m-auto"
      >
        <p class="text-[32px] leading-[40px]">Something went wrong</p>
        <p class="text-[20px] leading-[28px] font-aktiv-grotesk mt-[16px]">
          We couldn't complete your login.
        </p>
        <button
          class="bg-[#00A9E0] text-white px-[40px] py-[16px] text-[20px] leading-[24px] rounded-[40px] font-aktiv-grotesk-medium mt-[24px]"
          @click="goToHome()"
        >
          Try again
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonButton,
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/vue";
import axios from "axios";
import { Browser } from "@capacitor/browser";
import { defineComponent } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { mapActions } from "vuex";
import {
  createRedirectUri,
  getLoginPathObj,
  clearLoginPathObj,
} from "../../utils/redirect-uri";
import { useRoute, useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { initLoginTracking } from "@/utils/login-tracking.js";
import LoadingState from "@/components/common/LoadingState.vue";
import LocalLogin from "@/components/Local/localLogin.vue";
import { datadogLogs } from "@datadog/browser-logs";

export default defineComponent({
  name: "HomePage",
  components: {
    IonButton,
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    LoadingState,
    LocalLogin,
  },

  setup() {
    const {
      loginWithRedirect,
      isAuthenticated,
      user,
      checkSession,
      handleRedirectCallback,
    } = useAuth0();
    const logoutUri = createRedirectUri(
      process.env.VUE_APP_PACKAGE_ID,
      process.env.VUE_APP_AUTH0_DOMAIN
    );
    const route = useRoute();
    const router = useRouter();

    return {
      isAuthenticated,
      user,
      checkSession,
      route,
      router,
      handleRedirectCallback,
    };
  },
  data() {
    return {
      token: "",
      error: false,
    };
  },
  async mounted() {
    console.log("Callback is Mounted");

    try {
      initLoginTracking(this.user ? this.user.sub : "");
    } catch {
      console.error("Error init logging tracking");
    }

    const maxRetries = 3;
    const baseDelay = 5000; // 1 second

    const attemptCallback = async (attempt = 1) => {
      try {
        console.log(`Callback attempt ${attempt}/${maxRetries}`);

        const appState = await this.handleRedirectCallback();
        if (appState && appState.fullPathObj) {
          this.router.push({
            path: appState.fullPathObj.path,
            query: appState.fullPathObj.query,
            hash: appState.fullPathObj.hash,
          });
        }
        return true; // Success
      } catch (err) {
        const userId = this.user ? this.user.sub : "";
        console.log(`Attempt ${attempt} failed:`, err.name, err.message);
        if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
          const message = `User (auth0Id: ${userId}) login attempt ${attempt} failed:`;
          const data = {
            auth0Id: userId,
            logType: "login",
            errMesage: err.message,
          };
          datadogLogs.logger.warn(message, data);
        }
        attempt += 1;

        if (err.message === "Invalid state") {
          console.log("Invalid state error - forwarding after login");
          this.forwardAfterLogin();
          return true; // Handle this as success since we're forwarding
        }

        if (attempt >= maxRetries) {
          const message = `User (auth0Id: ${userId}): Max retries reached for handleRedirectCallback`;
          if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
            const message = `User (auth0Id: ${userId}) still in login screen in ${minToWait} min `;
            const data = { auth0Id: userId, logType: "login" };
            datadogLogs.logger.error(message, data);
          }
          throw err; // Re-throw after max retries
        }

        // Exponential backoff: wait longer between retries
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));

        return attemptCallback(attempt + 1);
      }
    };

    try {
      await attemptCallback();
    } catch (finalErr) {
      console.error("All callback attempts failed:", finalErr);
      this.error = true;
    }

    // this is to handle if they refresh when they are stuck in callback
    if (this.isAuthenticated) {
      console.log("User Mounted logged in");
      this.forwardAfterLogin();
    }

    // this is access denied code, if more than 1 error, can put it in function
    if (this.route.query.error === "access_denied") {
      console.warn("inside access denied error");
      const errorDescription = this.route.query.error_description;
      console.warn("error description", errorDescription);
      if (errorDescription === "access denied") {
        this.router.push("/access-denied");
      }
    }
  },
  methods: {
    ...mapActions("user", ["setToken"]),
    // TODO: clean up the rest below
    forwardAfterLogin() {
      const loginPathObj = getLoginPathObj();
      if (loginPathObj && loginPathObj.path !== "/") {
        console.log("forwarding login path:", JSON.stringify(loginPathObj));
        this.$router.push({
          path: loginPathObj.path,
          query: loginPathObj.query,
          hash: loginPathObj.hash,
        });
      } else {
        this.router.push("/home");
      }
    },
    goToHome() {
      this.$router.push("/home");
    },
  },
});
</script>

<style scoped>
#container {
  text-align: center;

  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

#container strong {
  font-size: 20px;
  line-height: 26px;
}

#container p {
  font-size: 16px;
  line-height: 22px;

  color: #8c8c8c;

  margin: 0;
}

#container a {
  text-decoration: none;
}
</style>
